import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlusCircle, Trash2, Pencil, Folder, List, Loader2 } from 'lucide-react';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { MarkdownEditor } from '@/components/blog/MarkdownEditor';

// Added interface for blog groups to ensure type safety
interface BlogGroup {
  id?: string;
  name: string;
  description: string | null;
  sort_order: number;
}

// Added interface for blog posts to ensure type safety
interface BlogPost {
  id?: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  read_time: string;
  group_id: string | null;
  image: string;
  created_at?: string;
  group_name?: string;
  date?: string; // Added the date property that was missing
}

const BlogManagement = () => {
  // Blog Posts State
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [blogGroups, setBlogGroups] = useState<BlogGroup[]>([]);
  const [formData, setFormData] = useState<BlogPost>({
    title: '',
    content: '',
    excerpt: '',
    author: '',
    read_time: '',
    group_id: '',
    image: ''
  });

  // Blog Groups State
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [isGroupEditMode, setIsGroupEditMode] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<BlogGroup | null>(null);
  const [groupFormData, setGroupFormData] = useState<BlogGroup>({
    name: '',
    description: null,
    sort_order: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // UI State
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('posts');

  useEffect(() => {
    fetchBlogPosts();
    fetchBlogGroups();
  }, [searchParams]);

  const fetchBlogPosts = async () => {
    try {
      setIsLoading(true);
      
      const response = await axios.get('/api/blog-posts');
      const data = response.data;
      
      // Make sure data is an array
      const postsArray = Array.isArray(data) ? data : [];
      
      setBlogPosts(postsArray.map((post: any) => ({
        ...post,
        date: new Date(post.created_at).toLocaleDateString(),
      })));
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setBlogPosts([]);
      toast({
        title: "Error",
        description: "Failed to load blog posts",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBlogGroups = async () => {
    try {
      console.log('Fetching blog groups...');
      const response = await axios.get('/api/blog-groups');
      console.log('Blog groups response:', response.data);
      // Make sure data is an array
      setBlogGroups(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching blog groups:', error);
      setBlogGroups([]);
      toast({
        title: "Error",
        description: "Failed to load blog groups",
        variant: "destructive"
      });
    }
  };

  // Blog Post Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContentChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      content: value
    }));
  };

  const handleGroupChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      group_id: value === "none" ? "" : value
    }));
  };

  const handleAddPost = () => {
    setIsEditMode(false);
    setSelectedPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      author: '',
      read_time: '',
      group_id: '',
      image: ''
    });
    setIsDialogOpen(true);
  };

  const handleEditPost = (post: BlogPost) => {
    setIsEditMode(true);
    setSelectedPost(post);
    setFormData({
      title: post.title,
      content: post.content || '',
      excerpt: post.excerpt || '',
      author: post.author || '',
      read_time: post.read_time || '',
      group_id: post.group_id || '',
      image: post.image || ''
    });
    setIsDialogOpen(true);
  };

  const handleDeletePost = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`/api/blog-posts/${id}`);
      
      toast({
        title: "Success",
        description: "Blog post deleted successfully",
      });
      
      fetchBlogPosts();
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast({
        title: "Error",
        description: "Failed to delete blog post",
        variant: "destructive"
      });
    }
  };

  const handleSavePost = async () => {
    try {
      if (isEditMode && selectedPost) {
        await axios.put(`/api/blog-posts/${selectedPost.id}`, {
          title: formData.title,
          content: formData.content,
          excerpt: formData.excerpt,
          author: formData.author,
          read_time: formData.read_time,
          group_id: formData.group_id || null,
          image: formData.image
        });
        
        toast({
          title: "Success",
          description: "Blog post updated successfully",
        });
      } else {
        await axios.post('/api/blog-posts', {
          title: formData.title,
          content: formData.content,
          excerpt: formData.excerpt,
          author: formData.author,
          read_time: formData.read_time,
          group_id: formData.group_id || null,
          image: formData.image
        });
        
        toast({
          title: "Success",
          description: "Blog post created successfully",
        });
      }
      
      setIsDialogOpen(false);
      fetchBlogPosts();
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast({
        title: "Error",
        description: "Failed to save blog post",
        variant: "destructive"
      });
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.url) {
        const imageUrl = response.data.url;
        const imageMarkdown = `![${file.name}](${imageUrl})`;
        
        setFormData(prev => ({
          ...prev,
          content: prev.content + '\n\n' + imageMarkdown,
          image: prev.image || imageUrl
        }));
        
        toast({
          title: "Success",
          description: "Image uploaded successfully",
        });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    }
  };

  // Blog Group Handlers
  const handleGroupInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setGroupFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddGroup = () => {
    setIsGroupEditMode(false);
    setSelectedGroup(null);
    setGroupFormData({
      name: '',
      description: null,
      sort_order: 0
    });
    setIsGroupDialogOpen(true);
  };

  const handleEditGroup = (group: BlogGroup) => {
    setIsGroupEditMode(true);
    setSelectedGroup(group);
    setGroupFormData({
      name: group.name || '',
      description: group.description || null,
      sort_order: group.sort_order || 0
    });
    setIsGroupDialogOpen(true);
  };

  const handleDeleteGroup = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;

    try {
      await axios.delete(`/api/blog-groups/${id}`);
      
      toast({
        title: "Success",
        description: "Blog group deleted successfully",
      });
      
      fetchBlogGroups();
    } catch (error) {
      console.error('Error deleting blog group:', error);
      toast({
        title: "Error",
        description: "Failed to delete blog group",
        variant: "destructive"
      });
    }
  };

  const handleSaveGroup = async () => {
    try {
      // Validate the group name
      if (!groupFormData.name.trim()) {
        toast({
          title: "Validation Error",
          description: "Group name is required.",
          variant: "destructive",
        });
        return;
      }
      
      setIsSubmitting(true);
      
      console.log('Saving blog group with data:', groupFormData);
      
      // Make sure sort_order is a number
      const dataToSend = {
        ...groupFormData,
        sort_order: Number(groupFormData.sort_order) || 0
      };
      
      let response;
      if (isGroupEditMode && selectedGroup?.id) {
        response = await axios.put(`/api/blog-groups/${selectedGroup.id}`, dataToSend);
        console.log('Group update response:', response);
        
        toast({
          title: "Success",
          description: "Blog group updated successfully",
        });
      } else {
        response = await axios.post('/api/blog-groups', dataToSend);
        console.log('Group creation response:', response);
        
        toast({
          title: "Success",
          description: "Blog group created successfully",
        });
      }
      
      setIsGroupDialogOpen(false);
      await fetchBlogGroups(); // Make sure to await this to refresh the list
    } catch (error) {
      console.error('Error saving blog group:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save blog group",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Blog Management</h1>
          <div className="flex space-x-2">
            <Button onClick={() => setActiveTab('posts')}>
              <List className="mr-2 h-4 w-4" />
              Posts
            </Button>
            <Button onClick={() => setActiveTab('groups')}>
              <Folder className="mr-2 h-4 w-4" />
              Groups
            </Button>
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="posts">Blog Posts</TabsTrigger>
            <TabsTrigger value="groups">Blog Groups</TabsTrigger>
          </TabsList>
          
          <TabsContent value="posts" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={handleAddPost}>
                <PlusCircle className="mr-2 h-4 w-4" />
                New Post
              </Button>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Group</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4">Loading...</TableCell>
                    </TableRow>
                  ) : blogPosts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4">No blog posts found</TableCell>
                    </TableRow>
                  ) : (
                    blogPosts.map((post, index) => (
                      <TableRow key={post.id}>
                        <TableCell className="font-medium">{index + 1}</TableCell>
                        <TableCell>{post.title}</TableCell>
                        <TableCell>{post.author}</TableCell>
                        <TableCell>{post.group_name || '-'}</TableCell>
                        <TableCell>{post.date}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEditPost(post)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeletePost(post.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          
          <TabsContent value="groups" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={handleAddGroup}>
                <PlusCircle className="mr-2 h-4 w-4" />
                New Group
              </Button>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Sort Order</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blogGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4">No blog groups found</TableCell>
                    </TableRow>
                  ) : (
                    blogGroups.map((group, index) => (
                      <TableRow key={group.id}>
                        <TableCell className="font-medium">{index + 1}</TableCell>
                        <TableCell>{group.name}</TableCell>
                        <TableCell>{group.description || '-'}</TableCell>
                        <TableCell>{group.sort_order}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEditGroup(group)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteGroup(group.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Blog Post Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby="post-dialog-description">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Edit Blog Post' : 'New Blog Post'}
            </DialogTitle>
            <DialogDescription id="post-dialog-description">
              {isEditMode ? 'Edit the details of this blog post.' : 'Create a new blog post for your website.'}
            </DialogDescription>
          </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Post title"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="group">Group</Label>
                  <Select onValueChange={handleGroupChange} value={formData.group_id}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {blogGroups.map(group => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="author">Author</Label>
                  <Input
                    id="author"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    placeholder="Author name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="read_time">Read Time</Label>
                  <Input
                    id="read_time"
                    name="read_time"
                    value={formData.read_time}
                    onChange={handleInputChange}
                    placeholder="e.g. 5 min read"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="excerpt">Excerpt</Label>
                <Input
                  id="excerpt"
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  placeholder="Brief excerpt of the post"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="image">Featured Image URL</Label>
                <Input
                  id="image"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="space-y-2">
                <Label>Content</Label>
                <div className="border rounded-md">
                  <MarkdownEditor
                    value={formData.content}
                    onChange={handleContentChange}
                    onImageUpload={handleImageUpload}
                  />
                </div>
              </div>
            </div>
          <DialogFooter className="sticky bottom-0 bg-background pt-2 pb-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePost}>
              {isEditMode ? 'Update' : 'Create'} Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Blog Group Dialog */}
      <Dialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="group-dialog-description">
          <DialogHeader>
            <DialogTitle>
              {isGroupEditMode ? 'Edit Blog Group' : 'New Blog Group'}
            </DialogTitle>
            <DialogDescription id="group-dialog-description">
              {isGroupEditMode ? 'Edit the details of this blog group.' : 'Create a new blog group to categorize your posts.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Group Name</Label>
              <Input
                id="name"
                name="name"
                value={groupFormData.name}
                onChange={handleGroupInputChange}
                placeholder="Group name"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={groupFormData.description || ''}
                onChange={handleGroupInputChange}
                placeholder="Group description"
                rows={3}
                disabled={isSubmitting}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                name="sort_order"
                type="number"
                value={groupFormData.sort_order}
                onChange={handleGroupInputChange}
                placeholder="0"
                disabled={isSubmitting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGroupDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSaveGroup} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                isGroupEditMode ? 'Update Group' : 'Create Group'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default BlogManagement;
